import { json, jsonParseLinter } from '@codemirror/lang-json'
import { linter } from '@codemirror/lint'
import {
  Alert,
  Button,
  CodeEditorV2,
  Dialog,
  Input,
  RadixTabs,
  Select,
} from '@portal/components'
import { isArray } from 'lodash'
import React, { useState } from 'react'

import { escapeApostrophesInJson, validateJsonObjectContent } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'
import { DataImportFile } from './DataImportFile'
import type { ImportMethod } from './types'

type CollectionSelectorProps = {
  appCollections: string[]
  selectedCollection: string
  createNewCollection: string
  onSelectCollection: (value: string) => void
  onCreateCollection: (e: React.ChangeEvent<HTMLInputElement>) => void
}

const CollectionSelector = ({
  appCollections,
  selectedCollection,
  createNewCollection,
  onSelectCollection,
  onCreateCollection,
}: CollectionSelectorProps) => {
  const [tab, setTab] = useState<'existing' | 'new'>('existing')

  const handleTabChange = (value: string) => {
    if (value === 'existing' || value === 'new') {
      setTab(value)
    }
  }

  if (!appCollections?.length) {
    return (
      <Input
        label="Create a new collection"
        placeholder="Collection name"
        value={createNewCollection}
        onChange={onCreateCollection}
        data-testid="create-collection-input"
      />
    )
  }

  return (
    <>
      <RadixTabs
        defaultValue="existing"
        value={tab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <RadixTabs.List className="w-full">
          <RadixTabs.Trigger value="existing" className="w-full">
            Existing Collection
          </RadixTabs.Trigger>
          <RadixTabs.Trigger value="new" className="w-full">
            New Collection
          </RadixTabs.Trigger>
        </RadixTabs.List>
      </RadixTabs>

      {tab === 'existing' && (
        <Select
          value={selectedCollection}
          label="Select a collection"
          onValueChange={onSelectCollection}
          options={appCollections.map((c) => ({
            label: c,
            value: c,
          }))}
          placeholder="Select a collection"
          aria-label="Select a collection"
        />
      )}

      {tab === 'new' && (
        <Input
          label="Create a new collection"
          placeholder="Collection name"
          value={createNewCollection}
          onChange={onCreateCollection}
          data-testid="create-collection-input"
        />
      )}
    </>
  )
}

type ImportMethodContentProps = {
  importMethod: ImportMethod
  importData: string | undefined
  uploadedFile: File | undefined
  isLoading: boolean
  onFileDropped: (file?: File, contents?: string) => void
  onImportDataChange: (value: string) => void
}

const ImportMethodContent = ({
  importMethod,
  importData,
  uploadedFile,
  isLoading,
  onFileDropped,
  onImportDataChange,
}: ImportMethodContentProps) => {
  const jsonLinter = linter(jsonParseLinter())

  if (importMethod === 'file') {
    return (
      <DataImportFile
        uploadedFile={uploadedFile}
        onDroppedFile={onFileDropped}
        disabled={isLoading}
        className="h-[256px]"
      />
    )
  }

  if (importMethod === 'json') {
    return (
      <div className="w-full overflow-hidden" data-testid="importByJsonSection">
        <div
          className="flex h-[240px] w-full flex-1 overflow-hidden rounded-lg border border-border-normal drop-shadow-sm"
          data-testid="codeEditor"
        >
          <CodeEditorV2
            height="240px"
            basicSetup={{
              lineNumbers: true,
              highlightActiveLineGutter: false,
            }}
            value={importData}
            language={json()}
            onChange={onImportDataChange}
            linter={jsonLinter}
            className="w-full"
          />
        </div>
      </div>
    )
  }

  return null
}

type DataImportDialogProps = {
  /**
   * True if the dialog is open.
   */
  isOpen: boolean
  /**
   * Callback to be called when the dialog is closed.
   */
  onClose: () => void
  /**
   * The method by which the data will be imported.
   */
  importMethod?: ImportMethod
}

export const DataImportDialog = ({
  isOpen,
  onClose,
  importMethod = 'file',
}: DataImportDialogProps) => {
  const { appCollections, isLoading, setQuery } = useDQLEditorContext()

  const [collectionState, setCollectionState] = useState({
    selected: '',
    new: '',
  })
  const [importState, setImportState] = useState({
    data: undefined as string | undefined,
    file: undefined as File | undefined,
    error: '',
  })

  const handleClose = () => {
    setCollectionState({ selected: '', new: '' })
    setImportState({ data: undefined, file: undefined, error: '' })
    onClose()
  }

  const handleCreateCollection = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCollectionState({ selected: '', new: e.target.value })
  }

  const handleSelectCollection = (value: string) => {
    setCollectionState({ selected: value, new: '' })
  }

  const handleRunImportQuery = () => {
    if (isLoading || !importState.data) return

    const data = !importState.file
      ? importState.data
      : JSON.stringify(importState.data)

    if (!validateJsonObjectContent(data)) {
      setImportState((prev) => ({
        ...prev,
        error: 'Error importing data: Invalid format',
      }))
      return
    }

    const collectionName = collectionState.selected || collectionState.new
    const collection = `\`${collectionName}\``

    if (!isArray(JSON.parse(data))) {
      const escapedData = JSON.stringify(
        escapeApostrophesInJson(JSON.parse(data)),
      )
      setQuery(
        `INSERT INTO ${collection} DOCUMENTS (deserialize_json('${escapedData}'))`,
      )
    } else {
      const jsonInserts = JSON.parse(data).map(
        (json: Record<string, unknown>) => {
          const escapedJson = escapeApostrophesInJson(json)
          return `(deserialize_json('${JSON.stringify(escapedJson)}'))`
        },
      )

      setQuery(`INSERT INTO ${collection} DOCUMENTS ${jsonInserts.join(', ')}`)
    }

    handleClose()
  }

  const handleFileDropped = (file?: File, contents?: string) => {
    if (file && contents) {
      setImportState({ data: contents, file, error: '' })
    } else {
      setImportState({ data: undefined, file: undefined, error: '' })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <Dialog.Content className="space-y-5">
        <Dialog.Header>
          <Dialog.Title>{`Import data by ${importMethod}`}</Dialog.Title>
        </Dialog.Header>

        <CollectionSelector
          appCollections={appCollections || []}
          selectedCollection={collectionState.selected}
          createNewCollection={collectionState.new}
          onSelectCollection={handleSelectCollection}
          onCreateCollection={handleCreateCollection}
        />

        <ImportMethodContent
          importMethod={importMethod}
          importData={importState.data}
          uploadedFile={importState.file}
          isLoading={isLoading}
          onFileDropped={handleFileDropped}
          onImportDataChange={(value) =>
            setImportState((prev) => ({ ...prev, data: value }))
          }
        />

        {importState.error && (
          <Alert variant="danger">{importState.error}</Alert>
        )}

        <Dialog.Footer>
          <Button variant="ghost" onClick={handleClose}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={
              !!importState.error ||
              (!collectionState.selected && !collectionState.new) ||
              !importState.data
            }
            onClick={handleRunImportQuery}
          >
            Import
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default DataImportDialog
